import { classNames } from "primereact/utils";
import styles from "../WorkoutPlanDialog.module.scss";
import { Icon, IconType } from "../../../icons/Icon";
import {
  DistanceUnit,
  IWorkoutSetViewModel,
  WorkoutExerciseType,
} from "../models/workoutPlanViewModel";
import { SetUpdateHandler, useSetRow } from "../hooks/useSetRow";
import { WorkoutAchievementTypeLabels } from "../../../labels";
import { formatPace, formatSetSummary, trimDecimal } from "../utils/setValueFormat";

interface IWorkoutPlanSetRowProps {
  set: IWorkoutSetViewModel;
  setIndex: number;
  isReadOnly: boolean;
  exerciseType: WorkoutExerciseType;
  distanceUnit?: DistanceUnit;
  /** A resistance-band exercise: the row gets a band resistance (kg) input first. */
  isResistanceBand?: boolean;
  supersetLetter: string | null;
  onUpdate: SetUpdateHandler;
  onToggleCompleted: (setId: number, isCompleted: boolean) => Promise<void> | void;
  onRemove: (setId: number) => Promise<void> | void;
}

/**
 * Renders one set row. Layout is driven by the parent exercise's measurement
 * type so the same data model (Kg + Reps) gets relabelled and re-arranged
 * without touching the persistence shape:
 *
 *   - KgReps:   "previous" + Kg input + Reps input
 *   - Time:     "previous" + minutes:seconds (Kg stays at 0, hidden)
 *   - Distance: "previous" + Kg input + metres / kilometres input
 *   - TimePace: "previous" + minutes:seconds + km/h input (with the min/km pace under it)
 *
 * A resistance-band exercise adds a band resistance (kg) input before those.
 */
export const WorkoutPlanSetRow = ({
  set,
  setIndex,
  isReadOnly,
  exerciseType,
  distanceUnit = DistanceUnit.Meters,
  isResistanceBand = false,
  supersetLetter,
  onUpdate,
  onToggleCompleted,
  onRemove,
}: IWorkoutPlanSetRowProps) => {
  // Trainees may edit the reps of a kg×reps exercise (e.g. "I did 8, not 10"),
  // but nothing else: the weight stays locked, and the seconds/metres of a
  // time/distance exercise — which live in this same reps column — stay locked
  // too. Trainers (not read-only) can edit every field as before.
  const repsLocked =
    isReadOnly && exerciseType !== WorkoutExerciseType.KgReps;

  const {
    kgInput,
    repsInput,
    minutesInput,
    secondsInput,
    resistanceInput,
    speedInput,
    handleKgChange,
    handleRepsChange,
    handleMinutesChange,
    handleSecondsChange,
    handleResistanceChange,
    handleSpeedChange,
    flushPending,
  } = useSetRow({
    set,
    disabled: repsLocked,
    onUpdate,
    exerciseType,
    distanceUnit,
    isResistanceBand,
  });

  const setNumber = `${supersetLetter ?? ""}${setIndex + 1}`;
  const achievements = set.achievements ?? [];
  const hasRecord = set.isCompleted && achievements.length > 0;
  const recordTitle = achievements
    .map((type) => WorkoutAchievementTypeLabels[type])
    .join(" · ");

  // The "previous" pill summarises the matching set of the user's last workout
  // in the appropriate unit. Wording follows the row's current measurement
  // system (lifting numbers / time / distance / time + speed) so it's never ambiguous.
  const previousLabel =
    formatSetSummary(exerciseType, distanceUnit, {
      kg: set.previousKg,
      reps: set.previousReps,
      speedKmh: set.previousSpeedKmh,
    }) ?? "—";
  const previousBand =
    isResistanceBand && set.previousResistanceKg
      ? `ластик ${trimDecimal(set.previousResistanceKg)}кг`
      : null;

  const isTimed =
    exerciseType === WorkoutExerciseType.Time ||
    exerciseType === WorkoutExerciseType.TimePace;
  const showsKg =
    exerciseType === WorkoutExerciseType.KgReps ||
    exerciseType === WorkoutExerciseType.Distance;

  const repsAriaLabel =
    exerciseType === WorkoutExerciseType.Distance
      ? distanceUnit === DistanceUnit.Kilometers
        ? "Километри"
        : "Метри"
      : "Повторения";

  const selectOnFocus = (e: React.FocusEvent<HTMLInputElement>) => e.target.select();
  const pace = exerciseType === WorkoutExerciseType.TimePace ? formatPace(set.speedKmh) : null;

  return (
    <div className={styles.set_row}>
      <div className={styles.set_n}>
        {setNumber}
        {hasRecord && (
          <span
            className={styles.set_record}
            title={recordTitle}
            aria-label={`Рекорд: ${recordTitle}`}
            role="img"
          >
            <Icon
              type={IconType.Trophy}
              width={12}
              height={12}
              color="rgba(252, 225, 115, 0.95)"
            />
          </span>
        )}
      </div>
      <div className={styles.set_prev}>
        {previousLabel}
        {previousBand && <span className={styles.set_prev_extra}>{previousBand}</span>}
      </div>
      {isResistanceBand && (
        <input
          className={styles.set_inp}
          value={resistanceInput}
          placeholder="0"
          inputMode="decimal"
          disabled={isReadOnly}
          onChange={(e) => handleResistanceChange(e.target.value)}
          onFocus={selectOnFocus}
          onBlur={flushPending}
          aria-label="Съпротивление на ластика (кг)"
        />
      )}
      {showsKg && (
        <input
          className={styles.set_inp}
          value={kgInput}
          placeholder="0"
          inputMode="decimal"
          disabled={isReadOnly}
          onChange={(e) => handleKgChange(e.target.value)}
          onFocus={selectOnFocus}
          onBlur={flushPending}
          aria-label="Килограми"
        />
      )}
      {isTimed ? (
        <div className={styles.set_time}>
          <input
            className={styles.set_inp}
            value={minutesInput}
            placeholder="0"
            inputMode="numeric"
            disabled={repsLocked}
            onChange={(e) => handleMinutesChange(e.target.value)}
            onFocus={selectOnFocus}
            onBlur={flushPending}
            aria-label="Минути"
          />
          <span className={styles.set_time_sep} aria-hidden>
            :
          </span>
          <input
            className={styles.set_inp}
            value={secondsInput}
            placeholder="00"
            inputMode="numeric"
            disabled={repsLocked}
            onChange={(e) => handleSecondsChange(e.target.value)}
            onFocus={selectOnFocus}
            onBlur={flushPending}
            aria-label="Секунди"
          />
        </div>
      ) : (
        <input
          className={styles.set_inp}
          value={repsInput}
          placeholder="0"
          inputMode={
            exerciseType === WorkoutExerciseType.Distance && distanceUnit === DistanceUnit.Kilometers
              ? "decimal"
              : "numeric"
          }
          disabled={repsLocked}
          onChange={(e) => handleRepsChange(e.target.value)}
          onFocus={selectOnFocus}
          onBlur={flushPending}
          aria-label={repsAriaLabel}
        />
      )}
      {exerciseType === WorkoutExerciseType.TimePace && (
        <div className={styles.set_speed}>
          <input
            className={styles.set_inp}
            value={speedInput}
            placeholder="0"
            inputMode="decimal"
            disabled={repsLocked}
            onChange={(e) => handleSpeedChange(e.target.value)}
            onFocus={selectOnFocus}
            onBlur={flushPending}
            aria-label="Скорост (км/ч)"
          />
          {pace && <span className={styles.set_pace}>{pace}</span>}
        </div>
      )}
      <button
        type="button"
        className={classNames(styles.set_check, { [styles.set_check_done]: set.isCompleted })}
        onClick={() => onToggleCompleted(set.id, !set.isCompleted)}
        aria-label={set.isCompleted ? "Маркирай като незавършен" : "Маркирай като завършен"}
      >
        {set.isCompleted && <Icon type={IconType.Valid} color="rgba(119,255,198,0.9)" />}
      </button>
      {!isReadOnly && (
        <button
          type="button"
          className={styles.set_delete}
          onClick={() => onRemove(set.id)}
          aria-label="Премахни сет"
        >
          <Icon type={IconType.DeleteIcon} color="rgba(255,95,95,0.7)" />
        </button>
      )}
    </div>
  );
};
